/**
 * aigo-cart-drawer.js — AIGO cart drawer custom element
 *
 * Renders cart line items from /cart.js JSON (not Shopify section rendering).
 * Opens via:
 *   - cart:refresh event with { open: true } (quick-add, draft-order)
 *   - ProductForm.showCartDrawer patch (product page ATC)
 *   - .cart-drawer-button clicks (header bag icon)
 */
(function () {
  // ── Money formatting ────────────────────────────────────────────────────────
  function formatMoney(cents) {
    if (window.FoxTheme && FoxTheme.utils && FoxTheme.utils.formatMoney) {
      return FoxTheme.utils.formatMoney(cents, FoxTheme.routes.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }

  // ── Safe DOM helpers ────────────────────────────────────────────────────────
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) { node.setAttribute(k, attrs[k]); });
    if (children) {
      if (typeof children === 'string') {
        node.textContent = children;
      } else {
        children.forEach(function (c) { if (c) node.appendChild(c); });
      }
    }
    return node;
  }

  function txt(str) { return document.createTextNode(str); }

  // ── Fetch helpers ───────────────────────────────────────────────────────────
  function fetchCart() {
    return fetch(window.FoxTheme ? FoxTheme.routes.cart_url : '/cart.js', {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    }).then(function (r) { return r.json(); });
  }

  function changeQuantity(line, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    }).then(function (r) { return r.json(); });
  }

  // ── Custom element ──────────────────────────────────────────────────────────
  class AigoCartDrawer extends HTMLElement {
    connectedCallback() {
      this._scrim  = this.querySelector('.scrim--cart');
      this._drawer = this.querySelector('.aigo-drawer');
      this._body   = this.querySelector('.aigo-drawer__body');
      this._foot   = this.querySelector('.aigo-drawer__foot');
      this._subEl  = this.querySelector('.aigo-drawer__sub b');
      this._countEl = this.querySelector('.aigo-drawer__count');

      // Read settings from data attributes set by Liquid
      this._showLinePrices = this.dataset.showLinePrices !== 'false';

      // Close handlers
      var self = this;
      var closeBtn = this.querySelector('.aigo-drawer__x');
      if (closeBtn) closeBtn.addEventListener('click', function () { self.close(); });
      if (this._scrim) this._scrim.addEventListener('click', function () { self.close(); });

      // Footer action buttons
      var checkoutBtn = this.querySelector('.aigo-drawer__checkout');
      var viewCartBtn = this.querySelector('.aigo-drawer__viewcart');
      if (checkoutBtn) checkoutBtn.addEventListener('click', function () { window.location.href = '/checkout'; });
      if (viewCartBtn) viewCartBtn.addEventListener('click', function () { window.location.href = '/cart'; });

      // Listen for cart:refresh events
      this._onCartRefresh = function (e) { self.onCartRefresh(e); };
      document.addEventListener('cart:refresh', this._onCartRefresh);

      // Subscribe to Cart module for silent state-only updates (no auto-open)
      // cart:refresh handler (onCartRefresh) takes care of open:true cases
      if (window.FoxTheme && FoxTheme.Cart) {
        this._cartUnsub = FoxTheme.Cart.subscribe(function (payload) {
          // Only re-render when drawer is open — no point rebuilding hidden DOM
          if (self._drawer && !self._drawer.classList.contains('open')) return;
          self.render(payload.state);
        });
      }

      // Intercept header bag icon clicks to open drawer instead of navigating to /cart
      document.querySelectorAll('a.cart-drawer-button, a[class*="cart-drawer-button"]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          self.onCartRefresh({ detail: { open: true } });
        });
      });

      // Patch ProductForm instances: dispatch cart:refresh instead of calling Hyper's cart-drawer.show()
      document.querySelectorAll('form[is="product-form"]').forEach(function (form) {
        form.showCartDrawer = function () {
          document.dispatchEvent(
            new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } })
          );
        };
      });
    }

    disconnectedCallback() {
      document.removeEventListener('cart:refresh', this._onCartRefresh);
      if (this._cartUnsub) this._cartUnsub();
    }

    onCartRefresh(e) {
      var self = this;
      // Cart module has already fetched and stored latest state — use it directly
      var cart = window.FoxTheme && FoxTheme.Cart ? FoxTheme.Cart.getState() : null;
      if (cart) {
        self.render(cart);
        if (e && e.detail && e.detail.open) self.open();
      } else {
        // Fallback: Cart module not ready yet, fetch directly
        fetchCart().then(function (cart) {
          self.render(cart);
          if (e && e.detail && e.detail.open) self.open();
        }).catch(function (err) {
          console.error('[aigo-cart-drawer] fetchCart failed:', err);
        });
      }
      // Note: explicit pubsub.publish removed — Cart.subscribe() handles all re-renders.
    }

    render(cart) {
      // Update item count
      if (this._countEl) {
        this._countEl.textContent = cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
      }

      // Update subtotal (only when show_line_prices is enabled)
      if (this._subEl && this._showLinePrices) {
        this._subEl.textContent = formatMoney(cart.total_price);
      }

      // Render line items
      if (!this._body) return;

      // Clear existing content
      while (this._body.firstChild) this._body.removeChild(this._body.firstChild);

      if (cart.item_count === 0) {
        // Static empty-state HTML — no user data involved
        var emptyDiv = el('div', { class: 'aigo-drawer__empty' });
        emptyDiv.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>';
        emptyDiv.appendChild(el('p', { style: 'font-size:15px;font-weight:700;margin:0 0 6px' }, 'Your cart is empty'));
        emptyDiv.appendChild(el('p', { style: 'font-size:13px;margin:0 0 20px' }, 'Add some products to get started.'));
        emptyDiv.appendChild(el('a', { class: 'btn btn--primary', href: '/' }, 'Shop now'));
        this._body.appendChild(emptyDiv);
        return;
      }

      // Build each line item using DOM methods so user-supplied strings go through textContent
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        var lineIndex = i + 1;

        // Thumbnail
        var thumb = el('div', { class: 'dline__thumb' });
        if (window.FoxTheme && window.FoxTheme.proxyImageMap && window.FoxTheme.proxyImageMap[item.product_id]) {
          var proxyId = window.FoxTheme.proxyImageMap[item.product_id];
          var img = el('img', {
            'data-proxy-id': proxyId,
            src: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
            width: '64', height: '64', alt: ''
          });
          img.alt = item.title; // via property to auto-escape
          thumb.appendChild(img);
        } else if (item.image) {
          var imgSrc = item.image.replace(/(\.\w+)(\?.*)?$/, '_64x64_crop_center$1$2');
          var img = el('img', { src: imgSrc, width: '64', height: '64', alt: '' });
          img.alt = item.title; // via property to auto-escape
          thumb.appendChild(img);
        }

        // Name
        var nameEl = el('div', { class: 'dline__name' }, item.product_title);

        // Variant
        var varEl = null;
        if (item.variant_title && item.variant_title !== 'Default Title') {
          varEl = el('div', { class: 'dline__var' }, item.variant_title);
        }

        // Quantity stepper
        var decBtn = el('button', { type: 'button', class: 'qstep-dec', 'aria-label': 'Decrease quantity', 'data-line': String(lineIndex), 'data-key': item.key }, '−');
        var qtySpan = el('span', { class: 'qstep-qty' }, String(item.quantity));
        var incBtn = el('button', { type: 'button', class: 'qstep-inc', 'aria-label': 'Increase quantity', 'data-line': String(lineIndex), 'data-key': item.key }, '+');
        var qstep = el('div', { class: 'qstep' }, [decBtn, qtySpan, incBtn]);

        // Remove
        var rmBtn = el('button', { type: 'button', class: 'dline__rm', 'data-line': String(lineIndex), 'data-key': item.key }, 'Remove');

        var mid = el('div', { class: 'dline__mid' }, [nameEl, varEl, qstep, rmBtn]);

        // Price — only rendered when show_line_prices is enabled
        var priceEl = null;
        if (this._showLinePrices) {
          priceEl = el('div', { class: 'dline__price' });
          priceEl.textContent = formatMoney(item.line_price);
        }

        var dline = el('div', { class: 'dline', 'data-line': String(lineIndex) }, [thumb, mid, priceEl]);
        this._body.appendChild(dline);
      }

      // Load proxy images for any items that use the external proxy image system
      if (window.FoxTheme && typeof window.FoxTheme.loadProxyImages === 'function') {
        window.FoxTheme.loadProxyImages(this._body);
      }

      // Wire quantity + remove buttons
      this._wireButtons();
    }

    _wireButtons() {
      var self = this;
      this._body.querySelectorAll('.qstep-dec, .qstep-inc, .dline__rm').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var line = parseInt(btn.dataset.line, 10);
          var dline = self._body.querySelector('.dline[data-line="' + line + '"]');
          var currentQty = dline ? parseInt(dline.querySelector('.qstep-qty').textContent, 10) : 1;
          var newQty;
          if (btn.classList.contains('qstep-dec')) {
            newQty = Math.max(0, currentQty - 1);
          } else if (btn.classList.contains('qstep-inc')) {
            newQty = currentQty + 1;
          } else {
            newQty = 0; // remove
          }
          var lineKey = btn.dataset.key || null;

          btn.disabled = true;

          if (lineKey) {
            FoxTheme.Cart.change(lineKey, newQty)
              .catch(function (err) {
                console.error('[aigo-cart-drawer] changeQuantity failed:', err);
                btn.disabled = false;
              });
            // render happens via the Cart.subscribe callback in connectedCallback
          } else {
            // Fallback: Cart module not available or state stale, use direct fetch
            changeQuantity(line, newQty).then(function (cart) {
              self.render(cart);
            }).catch(function (err) {
              console.error('[aigo-cart-drawer] changeQuantity failed:', err);
              btn.disabled = false;
            });
          }
        });
      });
    }

    open() {
      if (!this._drawer) return;
      this._drawer.classList.add('open');
      if (this._scrim) this._scrim.classList.add('show');
      // iOS Safari ignores overflow:hidden on body — use position:fixed with scroll preservation
      this._savedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + this._savedScrollY + 'px';
      document.body.style.width = '100%';
    }

    close() {
      if (!this._drawer) return;
      this._drawer.classList.remove('open');
      if (this._scrim) this._scrim.classList.remove('show');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (this._savedScrollY !== undefined) {
        window.scrollTo(0, this._savedScrollY);
      }
    }
  }

  customElements.define('aigo-cart-drawer', AigoCartDrawer);
})();
